<template>
  <div
    class="experiences-wrapper"
    :class="{ 'experiences-wrapper_is-loading': categoryStore.isExperiencesLoading }"
  >
    <div
      v-if="categoryStore.experiences.length"
      class="experiences-sorting__container"
    >
      <Text
        as="span"
        :text="t('suggestions', categoryStore.experiencesCount)"
        :skeleton="categoryStore.isExperiencesLoading"
        :skeleton-breakpoints="{ default: '144px', tablet: '124px', mobile: '124px' }"
        data-test="exp-count-title"
      />
      <FilterSorting />
    </div>

    <template v-if="categoryStore.experiences.length">
      <div
        ref="experienceGrid"
        class="experiences-grid"
      >
        <template
          v-for="experience in categoryStore.experiences"
          :key="experience.id"
        >
          <div class="experiences-grid__cell">
            <ExpCard
              v-click-tracker="getParams(experience)"
              v-view-tracker="getParams(experience)"
              :exp="experience"
              :is-in-favorites="isInFavorites(experience.id)"
              :tooltip-teleport-target="expCardTooltipTeleportTarget"
              target-blank
              @toggle-favorite="toggleFavorite($event, experience)"
            />
          </div>
        </template>
      </div>
    </template>
    <template v-else>
      <EmptyBlock v-if="categoryStore.isInitialised && !categoryStore.isExperiencesLoading" />
    </template>

    <div
      v-if="categoryStore.pageCount > 1"
      class="experiences-pagination"
    >
      <UiPagination
        :page-count="categoryStore.pageCount"
        :current-page="categoryStore.currentPage"
        :is-show-more-button-hidden="false"
        @page-selected="categoryStore.setPage"
        @load-more-clicked="categoryStore.loadMore"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ExpCard } from '@experience/exp-card';
  import type { Experience } from '@experience/exp-card/dist/types/experience';
  import { useI18n } from '@experience/i18n';
  import { UiPagination } from '@experience/core-pagination-ui';
  import { computed, inject, ref } from 'vue';
  import { EmptyBlock } from '@/widgets/emptyBlock';
  import { trackEvent, useClickTracker, useViewTracker } from '@/services/analytics';

  import {
    deleteExperienceFromFavorite,
    type ExperienceFavoriteItem,
    putExperienceToFavorite,
  } from '@/shared/api/experience';
  import { useCategoryStore } from '@/shared/store';
  import { Text } from '@/shared/ui/text';

  import { FilterSorting } from '@/features/sorting';

  import { ru } from '@/widgets/experiencesList';

  const categoryStore = useCategoryStore();

  const shadowRoot = inject<ShadowRoot | null>('shadowRoot', null);
  const expCardTooltipTeleportTarget = computed(() => (shadowRoot?.host as HTMLElement | undefined) ?? undefined);

  const { t } = useI18n({
    inheritLocale: true,
    messages: { ru },
  });

  const vViewTracker = useViewTracker('category:experience.view');
  const vClickTracker = useClickTracker('category:experience.click');

  const experienceGrid = ref<HTMLElement | null>(null);
  /**
   * Проверка нахождения экспериенса в избранном.
   *
   * @param experienceId
   */
  const isInFavorites = (experienceId: number) =>
    !!categoryStore.experiencesFavorites?.find((item) => item.item_id === experienceId);

  /**
   * [method] обработка добавления в избранное
   * @param expId
   */
  const toggleMutex = ref(false);
  const toggleFavorite = async (expId: number, exp: Experience) => {
    trackEvent('category:experience:add-to-favorite.click', { ...getParams(exp), action: !isInFavorites(expId) });

    if (toggleMutex.value) {
      return;
    }
    toggleMutex.value = true;

    if (isInFavorites(expId)) {
      await deleteExperienceFromFavorite({
        path: { experienceId: `${expId}` },
      });

      categoryStore.experiencesFavorites = categoryStore.experiencesFavorites?.filter(
        (item: ExperienceFavoriteItem) => item.item_id !== expId,
      );
    } else {
      const { data } = await putExperienceToFavorite({
        path: {
          experienceId: `${expId}`,
        },
        body: {
          dt_created: new Date().toISOString(),
        },
      });

      if (!data) {
        return;
      }

      const newFavoriteItem = data;

      categoryStore.experiencesFavorites.push(newFavoriteItem);
    }

    toggleMutex.value = false;
  };

  const getParams = (params: Experience) => {
    const city = params.geo?.city?.[0];
    const country = params.geo?.country?.[0];

    return {
      category_title: categoryStore.seoCategoryTitle,
      category_id: categoryStore.seoCategoryId,
      city_id: city?.id,
      city_name: city?.name,
      country_id: country?.id,
      country_name: country?.name,
      discount_rate: params.price?.discount?.value,
      duration: params.duration,
      experience_id: params.id,
      format: params.format,
      full_price: params.price.value,
      guide_id: params.guide.id,
      instant_booking: params.is_instant_booking,
      rating: params.rating,
      title: params.annotation,
      type: params.type,
      is_paid: params.is_paid,
    };
  };
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography';
  @use '@experience/shared/dist/assets/adaptivity';

  .experiences-wrapper {
    position: relative;
    box-sizing: border-box;
    margin-top: 80px;
    padding-bottom: var(--alias-space-64);

    &_is-loading {
      &:before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        z-index: 10;
        width: 100%;
        height: 100%;
        background-color: rgba(255, 255, 255, 0.3);
      }
    }

    @include adaptivity.tablet {
      margin-top: 56px;
    }
  }

  .experiences-sorting__container {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-sizing: border-box;
    width: 100%;
    padding: 12px 0 24px;

    @include typography.base-400-400-medium;

    @include adaptivity.tablet {
      @include typography.base-300-300-medium;
    }
  }

  .experiences-grid {
    display: flex;
    flex-wrap: wrap;
    margin-right: -12px;
    margin-left: -12px;
  }

  .experiences-grid__cell {
    flex: 0 0 auto;
    box-sizing: border-box;
    width: 33.3333%;
    max-width: 100%;
    padding: 0 12px 40px;

    @include adaptivity.tablet {
      width: 50%;
    }

    @include adaptivity.mobile {
      width: 100%;
      padding: 0 12px 26px;
    }
  }

  .experiences-pagination {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    box-sizing: border-box;
    width: 100%;
    padding-top: var(--alias-space-32);

    // // запрещаяем UiPagination растягиваться шире контейнера
    :deep(.ui-pagination) {
      max-width: 100%;
    }
  }
</style>
